<template>
    <div class="purchases">
        <SettingsHead />
        <AddPaymentMethodSidePanel
            v-if="showAddPaymentMethodSidePanel"
            key="purchases"
            @close="showAddPaymentMethodSidePanel = false"
        />
        <EditSubscriptionSidePanel
            v-if="showEditSubscriptionSidePanel && subscriptionToEdit"
            :subscription="subscriptionToEdit"
            @close="showEditSubscriptionSidePanel = false"
        />
        <h2 v-dark class="purchases__label purchases__label--top">
            Premium Access
        </h2>
        <div
            v-if="!activeSubscriptions.length"
            v-dark
            class="purchases__no-subscriptions"
        >
            <img
                src="@/assets/settings/no-premium.png"
                alt=""
                class="purchases__no-subscriptions-image"
            >
            <div
                v-dark
                class="purchases__no-subscriptions-title"
                :class="{
                    'purchases__no-subscriptions-title--no-upgrade-to':
                        (currentExamMetadata && isDisabledBundle) ||
                        (currentExamMetadata && currentExamMetadata.isFree)
                }"
            >
                You have no active Pocket Prep purchases.
            </div>
            <div
                v-if="
                    (currentExamMetadata && !isDisabledBundle) &&
                        (currentExamMetadata && !currentExamMetadata.isFree)
                "
                v-dark
                class="purchases__no-subscriptions-upgrade"

            >
                <PocketLink
                    :is-dark-mode="isDarkMode"
                    type="tertiary-small"
                    @click="openUpgradeModal"
                >
                    Upgrade to Premium
                </PocketLink>
                to get the full Pocket Prep experience.
            </div>
            <img
                src="@/assets/settings/no-premium-mobile.png"
                alt=""
                class="purchases__no-subscriptions-image purchases__no-subscriptions-image--mobile"
            >
            <div
                v-dark
                class="purchases__no-subscriptions-help">
                Did you already pay for Premium and it’s not showing up here?
                <PocketLink
                    :is-dark-mode="isDarkMode"
                    class="purchases__no-subscriptions-help-link"
                    @click="showIntercom"
                >
                    Contact our support team.
                </PocketLink>
            </div>
        </div>
        <Table
            v-if="activeSubscriptions.length"
            class="purchases__subscriptions"
            theme="open"
            :show-header="false"
            :rows="activeSubscriptions"
            :columns="tableColumns"
            :is-dark-mode="isDarkMode"
            @sort="updateActiveSortSettings"
        >
            <template #tableCellValue="{ row, column }">
                <SubscriptionRow
                    :row="row"
                    :column="column"
                    @editSubscription="editSubscription"
                />
            </template>
        </Table>
        <div class="purchases__subscription-cards">
            <div
                v-for="sub in activeSubscriptions"
                :key="sub.id"
                v-dark
                class="purchases__subscription-card"
            >
                <div v-dark class="purchases__subscription-card-top">
                    <BundleIcon
                        :theme="isDarkMode ? 'silver' : 'color'"
                        :bundle-id="sub.bundleId"
                        class="purchases__subscription-card-icon"
                    />
                    <div class="purchases__subscription-card-top-right">
                        <div class="purchases__subscription-card-name">
                            {{ sub.name }}
                        </div>
                        <div v-dark class="purchases__subscription-card-name-subtext">
                            {{ sub.nameSubtext }}
                        </div>
                    </div>
                </div>
                <div v-dark class="purchases__subscription-card-info">
                    <div v-dark class="purchases__subscription-card-label">
                        Plan
                    </div>
                    <div class="purchases__subscription-card-title">
                        {{ sub.billingCycle ? sub.billingCycle : sub.planName }}
                    </div>
                    <div v-dark class="purchases__subscription-card-subtext">
                        <template v-if="sub.planCost && !sub.expired">
                            {{ sub.planCost }} •
                        </template>
                        {{ sub.renewsExpired }}
                    </div>
                </div>
                <div
                    v-if="sub.paymentPlatform"
                    v-dark
                    class="purchases__subscription-card-info"
                    :class="{ 'purchases__subscription-card-info--no-edit': !sub.canEdit }"
                >
                    <div v-dark class="purchases__subscription-card-label">
                        Paid via
                    </div>
                    <div class="purchases__subscription-card-title">
                        {{ sub.paymentPlatform }}
                    </div>
                    <div v-dark class="purchases__subscription-card-subtext">
                        {{ sub.paymentSubtext }}
                    </div>
                </div>
                <PocketButton
                    v-if="sub.canEdit"
                    type="tertiary-small"
                    class="purchases__subscription-card-edit"
                    :is-dark-mode="isDarkMode"
                    @click="editSubscription(sub.id)"
                >
                    Edit Purchase
                </PocketButton>
            </div>
        </div>
        <template v-if="subscriptions.expired.length">
            <h2 class="purchases__label">
                Expired
            </h2>
            <Table
                class="purchases__subscriptions"
                theme="open"
                :show-header="false"
                :rows="expiredSubscriptions"
                :columns="tableColumns"
                :is-dark-mode="isDarkMode"
                @sort="updateExpiredSortSettings"
            >
                <template #tableCellValue="{ row, column }">
                    <SubscriptionRow
                        :row="row"
                        :column="column"
                        @editSubscription="editSubscription"
                    />
                </template>
            </Table>
        </template>
        <h2 v-dark class="purchases__label">
            Wallet
        </h2>
        <div v-if="!paymentMethods.length" class="purchases__no-cards">
            You have no saved credit/debit cards.
        </div>
        <div class="purchases__cards">
            <div
                v-for="pm in paymentMethods"
                :key="pm.id"
                v-dark
                class="purchases__card"
                :class="{ 'purchases__card--expired': pm.hasExpired }"
            >
                <div class="purchases__card-title">
                    Card ending in {{ pm.last4 }}
                </div>
                <div v-dark class="purchases__card-expiration">
                    {{ pm.hasExpired ? 'Expired' : 'Expires' }}
                    {{ pm.expiration }}
                </div>
                <PocketButton
                    type="tertiary-small"
                    class="purchases__card-delete"
                    :disabled="isLoading"
                    :is-dark-mode="isDarkMode"
                    @click="paymentIdToDelete = pm.id"
                >
                    Delete
                </PocketButton>
            </div>
        </div>
        <PocketButton
            :is-dark-mode="isDarkMode"
            type="tertiary-small"
            @click="showAddPaymentMethodSidePanel = true"
        >
            + Add Card
        </PocketButton>
        <DeleteCardModal
            v-if="paymentIdToDelete"
            @close="paymentIdToDelete = null"
            @submit="deleteCard(paymentIdToDelete)"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import UIKit, { type ITableSortSettings } from '@pocketprep/ui-kit'
import type { Study } from '@pocketprep/types'
import AddPaymentMethodSidePanel from '@/components/Settings/AddPaymentMethodSidePanel.vue'
import EditSubscriptionSidePanel from '@/components/Settings/EditSubscriptionSidePanel.vue'
import SettingsHead from '@/components/Settings/SettingsHead.vue'
import SubscriptionRow from '@/components/Settings/Purchases/SubscriptionRow.vue'
import DeleteCardModal from '@/components/Settings/Purchases/DeleteCardModal.vue'
import { isSupportWindow, type TRow } from '@/utils'
import { subscriptionModule } from '@/store/subscription/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import { stripeModule } from '@/store/stripe/module'
import { userModule } from '@/store/user/module'
import { referralModule } from '@/store/referral/module'
import { toastModule } from '@/store/toast/module'
import { upgradeModule } from '@/store/upgrade/module'

const {
    Button: PocketButton,
    Link: PocketLink,
    Table,
    BundleIcon,
} = UIKit

const route = useRoute()

const showAddPaymentMethodSidePanel = ref(false)
const showEditSubscriptionSidePanel = ref(false)
const paymentIdToDelete = ref<null | string>(null)
const subscriptionToEdit = ref<null | ReturnType<Study.Class.SubscriptionBundle['toJSON']>>(null)
const activeSortSettings = ref<ITableSortSettings | null>(null)
const expiredSortSettings = ref<ITableSortSettings | null>(null)
const isLoading = ref(true)
const tableColumns = [{
    name: 'Purchase',
    propName: 'purchase',
}, {
    name: 'Plan',
    propName: 'plan',
}, {
    name: 'Paid via',
    propName: 'paid',
    isSortDisabled: true,
}, {
    name: 'Action',
    propName: 'action',
    isLabelHidden: true,
    isSortDisabled: true,
}]

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const paymentMethods = computed(() => {
    return stripeModule.getters.getStripePaymentMethods().map(pm => {
        return {
            id: pm.id,
            last4: pm.card?.last4,
            expiration: `${pm.card?.exp_month}/${String(pm.card?.exp_year).slice(-2)}`,
            hasExpired: new Date(pm.card?.exp_year || 0, (pm.card?.exp_month || 0) + 1, 0)
                .getTime() < new Date().getTime(),
        }
    })
})

const subscriptions = computed(() => {
    return subscriptionModule.state.subscriptions.reduce<{ active: TRow[]; expired: TRow[] }>((acc, s) => {
        const row: TRow = {
            id: s.objectId,
            source: '',
            bundleId: '',
            name: '',
            nameSubtext: '',
            planName: '',
            planCost: '',
            willAutoRenew: true,
            billingCycle: '',
            renewsExpired: '',
            paymentPlatform: '',
            paymentSubtext: '',
            canEdit: false,
            expired: false,
        }
        if ('bundle' in s) {
            row.bundleId = s.bundle.objectId
            const bundle = bundleModule.getters.getBundles().find(b => b.objectId === row.bundleId)
            const uniqueBundleExams = bundle && bundleModule.getters.getBundleUniqueExams(bundle.objectId)
            row.name = bundle?.name === 'Pocket Prep' ? bundle.name : `${bundle?.name} Pocket Prep`
            row.nameSubtext = `Premium access to ${uniqueBundleExams?.length} exam${
                (uniqueBundleExams?.length || 0) > 1 ? 's' : ''}`
        } else {
            const exam = examMetadataModule.getters.getMostRecentExamForExamGuid(s.examGuid)
            if (!exam) {
                return acc
            }
            row.bundleId = bundleModule.getters.getBundles()
                .find(b => b.exams.find(e => e.objectId === exam.objectId))?.objectId || ''
            row.name = exam.nativeAppName
            row.nameSubtext = 'Premium access to 1 exam'
        }

        if (!row.bundleId) {
            return acc
        }

        const receipt = s.receipt as Study.Class.ReceiptJSON
        row.source = receipt.source
        const expiresAt = new Date(s.expiresAt.iso)
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }
        row.willAutoRenew = s.willAutoRenew
        row.renewsExpired = (
            expiresAt.getTime() < new Date().getTime()
                ? 'Expired '
                : (s.willAutoRenew && (receipt.source !== 'Stripe' || paymentMethods.value.length))
                    ? 'Renews '
                    : 'Expires '
        ) + expiresAt.toLocaleDateString('en-US', options)
        if (s.days && s.days < 367 && s.willAutoRenew && receipt.source !== 'Stripe') {
            row.billingCycle = s.days > 362
                ? 'Billed Yearly'
                : s.days > 87
                    ? 'Billed Quarterly'
                    : 'Billed Monthly'
        }
        const expired = expiresAt.getTime() < new Date().getTime()
        row.expired = expired
        if (receipt.source === 'Grandfather') {
            row.planName = 'Legacy Premium'
            row.renewsExpired = ''
        } else if (receipt.source === 'Teach' || receipt.source === 'Bulk Purchase') {
            row.paymentPlatform = 'License Code'
            row.planName = 'Premium Prep'
            row.renewsExpired = ''
        } else if (receipt.source === 'Stripe') {
            const sub = stripeModule.getters.getStripeSubscriptions().find(ss => ss.id === s.guid)
            if (!sub) {
                if (row.name && expired) {
                    acc.expired.push(row)
                }
                return acc
            }
            if (sub) {
                const price = sub.items.data[0]?.price
                if (!price) {
                    throw new Error('Purchases - subscriptions: no price')
                }
                row.planName = 'Premium Prep'
                const planCostPrice = ((price.unit_amount || 0) / 100).toFixed(2)
                row.planCost = `$${planCostPrice}`
                if (price.recurring) {
                    row.billingCycle = price.recurring.interval === 'year'
                        ? 'Billed Yearly'
                        : price.recurring.interval_count === 1
                            ? 'Billed Monthly'
                            : 'Billed Quarterly'
                }
                const paymentMethod = stripeModule.getters.getStripePaymentMethods().find(
                    pm => pm.id === sub.default_payment_method
                )
                row.paymentPlatform = paymentMethod
                    ? `Card ending in ${paymentMethod.card?.last4}`
                    : 'Credit Card'
                // We do not want users to be able to edit (renew) expired One App purchases on Web
                row.canEdit = !(expired && row.name === 'Pocket Prep')
            }
        } else if (receipt.source === 'Google') {
            row.planName = row.billingCycle ? row.billingCycle : 'Premium Prep'

            const priceAmountMicros: string = receipt.data?.priceAmountMicros
            if (priceAmountMicros) {
                const googlePlanAmount = Number(priceAmountMicros)
                row.planCost = googlePlanAmount ? `$${googlePlanAmount / 1000000}` : ''
            }

            row.paymentPlatform = 'Google Play'
            row.paymentSubtext = expired
                ? 'Renew in Play Store'
                : 'Edit in Play Store'
            // We do not want users to be able to edit (renew) expired One App purchases on Web
            row.canEdit = expired && row.name !== 'Pocket Prep'
        } else if (receipt.source === 'Apple') {
            row.planName = row.billingCycle ? row.billingCycle : 'Premium Prep'
            row.paymentPlatform = 'Apple Subscription'
            row.paymentSubtext = expired
                ? 'Renew in App Store'
                : 'Edit in App Store'
            // We do not want users to be able to edit (renew) expired One App purchases on Web
            row.canEdit = expired && row.name !== 'Pocket Prep'
            const applePlanAmount = ((receipt.data.price || 0) / 1000).toFixed(2)
            row.planCost = applePlanAmount ? `$${applePlanAmount}` : ''
        } else if (receipt.source === 'Support') {
            row.paymentPlatform = 'Customer Support'
            row.planName = 'Premium Prep'
        } else if (receipt.source === 'Gift') {
            row.paymentPlatform = 'Gift'
            row.planName = 'Premium Prep'
        } else {
            row.planName = 'Premium Prep'
        }

        if (expired) {
            acc.expired.push(row)
        } else {
            acc.active.push(row)
        }
        return acc
    }, { active: [], expired: [] })
})

const expiredSubscriptions = computed(() => {
    const sort = expiredSortSettings.value
    return [ ...subscriptions.value.expired ].sort((a, b) => rowSort(a, b, sort))
})

const activeSubscriptions = computed(() => {
    const sort = activeSortSettings.value
    return [ ...subscriptions.value.active ].sort((a, b) => rowSort(a, b, sort))
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const examMetadataById = computed(() => {
    return examMetadataModule.getters.getExamMetadataById()
})

const bundle = computed(() => {
    return bundleModule.getters.getBundles()
        .find(b => b.exams.find(e => {
            const bundleExam = examMetadataById.value[e.objectId]
            return currentExamMetadata?.value && bundleExam?.examGuid === currentExamMetadata?.value.examGuid
        }))
})

const isDisabledBundle = computed(() => {
    return bundleModule.getters.isBundleDisabled(bundle.value?.name)
})

onMounted(async () => {
    await referralModule.actions.validateReferral()

    await Promise.all([
        stripeModule.actions.fetchStripePrices(),
        stripeModule.actions.fetchPaymentMethods(),
        stripeModule.actions.fetchStripeSubscriptions(),
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
        userModule.actions.fetchUserData(),
    ])

    const providedSubscriptionid = route.query.subscriptionId as string
    if (providedSubscriptionid) {
        editSubscription(providedSubscriptionid)
    }

    isLoading.value = false
})

const rowSort = (a: TRow, b: TRow, sort: ITableSortSettings | null) => {
    if (sort?.column?.name === 'Purchase') {
        return sort.direction * a.name.localeCompare(b.name)
    }
    if (sort?.column?.name === 'Plan') {
        return sort.direction * a.planName.localeCompare(b.planName)
    }

    return 0
}

const deleteCard = async (pmId: string) => {
    isLoading.value = true
    paymentIdToDelete.value = null
    await stripeModule.actions.deleteStripePM(pmId)
    toastModule.actions.displayToast({
        title: 'Card deleted.',
    })
    isLoading.value = false
}

const editSubscription = (subscriptionId: string) => {
    const subscription = subscriptionModule.state.subscriptions
        .find(s => s.objectId === subscriptionId && 'bundle' in s)

    if (subscription) {
        showEditSubscriptionSidePanel.value = true
        subscriptionToEdit.value = subscription as ReturnType<Study.Class.SubscriptionBundle['toJSON']>
    }
}

const openUpgradeModal = () => {
    upgradeModule.actions.openUpgradeModal()
}

const updateActiveSortSettings = (sortSettings: ITableSortSettings) => {
    activeSortSettings.value = sortSettings
}

const updateExpiredSortSettings = (sortSettings: ITableSortSettings) => {
    expiredSortSettings.value = sortSettings
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}
</script>

<style lang="scss" scoped>
.purchases {
    font-size: 16px;

    &__label {
        font-size: 16px;
        line-height: 19px;
        font-weight: 600;
        margin: 0 0 12px;

        &--dark {
            color: rgba($white, 0.86);
        }

        &--top {
            margin-top: 24px;
        }
    }

    &__no-cards {
        margin-bottom: 18px;
        font-size: 14px;
        line-height: 17px;
    }

    &__no-subscriptions {
        max-width: 788px;
        width: 100%;
        border-radius: 6px;
        padding: 34px 0 33px;
        background: $gray-background;
        text-align: center;
        margin-bottom: 87px;
        position: relative;
        overflow: hidden;

        &--dark {
            background-color: $charcoal;
        }

        @include breakpoint(grizzly-bear) {
            max-width: 730px;
        }

        @include breakpoint(brown-bear) {
            padding: 30px 0 20px;
        }

        @include breakpoint(black-bear) {
            padding: 32px 8px;
            box-sizing: border-box;
        }
    }

    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 100px;
        width: 392px;
        padding: 35px 0 26px;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        @include breakpoint(black-bear) {
            width: 356px;
            height: auto;
            max-height: 638px;
            top: 10px;
        }
    }

    &__modal-title {
        font-size: 18px;
        line-height: 22px;
        font-weight: 600;
        width: 309px;
        margin: 0 auto 12px;
    }

    &__modal-message {
        font-size: 15px;
        line-height: 22px;
        color: $slate-03;
        margin-bottom: 29px;
        width: 309px;

        &--dark {
            color: $white;
        }
    }

    &__no-subscriptions-image {
        position: absolute;
        left: 0;
        top: 0;
        height: 164px;

        @include breakpoint(grizzly-bear) {
            height: 181px;
        }

        @include breakpoint(black-bear) {
            display: none;
        }

        &--mobile {
            display: none;

            @include breakpoint(black-bear) {
                display: block;
                position: relative;
                margin: 0 auto 30px;
                left: auto;
                top: auto;
            }
        }
    }

    &__no-subscriptions-title {
        font-size: 16px;
        font-weight: 600;
        line-height: 19px;
        margin-bottom: 4px;
        position: relative;
        z-index: 1;

        &--dark {
            color: $white;
        }

        @include breakpoint(grizzly-bear) {
            max-width: 360px;
            margin: 0 auto 4px;
        }

        &--no-upgrade-to {
            margin-bottom: 18px;
        }
    }

    &__no-subscriptions-upgrade {
        font-size: 14px;
        line-height: 17px;
        margin-bottom: 18px;
        color: $slate-03;
        position: relative;
        z-index: 1;

        &--dark {
            color: $fog;
        }

        @include breakpoint(grizzly-bear) {
            max-width: 360px;
            margin: 0 auto 18px;
        }

        @include breakpoint(black-bear) {
            max-width: none;
            margin-bottom: 22px;
        }
    }

    &__no-subscriptions-help {
        font-size: 13px;
        line-height: 18px;
        color: $slate-01;
        max-width: 375px;
        margin: 0 auto;
        position: relative;
        z-index: 1;

        &--dark {
            color: $fog;
        }

        @include breakpoint(grizzly-bear) {
            max-width: 360px;
        }

        @include breakpoint(black-bear) {
            max-width: none;
        }

        a {
            font-size: 13px;
            line-height: 18px;
            margin: 0 auto;

            @include breakpoint(grizzly-bear) {
                display: inline;
                font-weight: 500;
            }
        }
    }

    &__subscriptions {
        margin-left: -12px;
        margin-right: -12px;
        max-width: 789px;
        margin-bottom: 48px;

        @include breakpoint(brown-bear) {
            max-width: 706px;
        }

        @include breakpoint(black-bear) {
            display: none;
        }

        :deep(.uikit-table__row) {
            padding-left: 0;
        }

        :deep(.uikit-table__column-label),
        :deep(.uikit-table__cell) {
            &:nth-child(1) {
                width: 299px;

                @include breakpoint(grizzly-bear) {
                    width: 269px;
                }

                @include breakpoint(brown-bear) {
                    width: 270px;
                }
            }

            &:nth-child(2) {
                width: 234px;

                @include breakpoint(grizzly-bear) {
                    width: 219px;
                }

                @include breakpoint(brown-bear) {
                    width: 213px;
                }
            }

            &:nth-child(3) {
                width: 176px;

                @include breakpoint(grizzly-bear) {
                    width: 159px;
                }

                @include breakpoint(brown-bear) {
                    width: 142px;
                }
            }

            &:nth-child(4) {
                width: 26px;
                margin-right: -12px;
            }
        }

        :deep(.uikit-table__column-label:first-child) {
            margin-right: 0;
        }
    }

    &__cards {
        margin-bottom: 20px;
        margin-left: -9px;
    }

    &__card {
        position: relative;
        width: 320px;
        height: 74px;
        border-radius: 6px;
        margin-bottom: 12px;
        padding: 16px 0 0 20px;
        box-sizing: border-box;
        border: 1px solid $fog;
        background: $white;

        &--dark {
            background: $moonlit-ocean;
            border-color: $moonlit-ocean;
            box-shadow: 0 1px 6px 0 rgba($charcoal, 0.3);
        }

        &--expired {
            border: 1px solid rgba($brand-red, 0.5);
            background: $red-hint;
            box-shadow: none;

            &--dark {
                background: rgba($brand-red, 0.3);
                border-color: rgba($rosa, 0.5);
            }
        }
    }

    &__card-title {
        font-size: 15px;
        line-height: 18px;
        font-weight: 600;
        margin-bottom: 4px;
    }

    &__card-expiration {
        font-size: 13px;
        line-height: 18px;
        color: $slate-01;

        &--dark {
            color: $fog;
        }
    }

    &__card-delete {
        position: absolute;
        right: 24px;
        top: 17px;
    }

    &__subscription-cards {
        display: none;
        margin-left: -12px;
        margin-right: -12px;
        margin-bottom: 48px;

        @include breakpoint(black-bear) {
            display: block;
        }
    }

    &__subscription-card {
        width: 381px;
        max-width: 100%;
        border-radius: 6px;
        padding: 16px 0 0;
        box-shadow: 0 1px 5px 0 rgba($brand-black, 0.2);
        margin-bottom: 12px;
        box-sizing: border-box;
        background: $white;

        @include breakpoint(black-bear) {
            border: 1px solid $fog;
        }

        &--dark {
            background: $brand-black;
            box-shadow: 0 1px 6px 0 rgba($jet, 0.3);
            border-color: $brand-black;
        }
    }

    &__subscription-card-top {
        display: flex;
        align-content: center;
        margin-bottom: 12px;
        border-bottom: 1px solid $fog;
        padding: 0 12px;

        &--dark {
            border-bottom-color: $pickled-bluewood;
        }
    }

    &__subscription-card-icon {
        width: 45px;
        min-width: 45px;
        height: 45px;
        margin-right: 8px;
        margin-top: -7px;
        margin-left: -6px;
    }

    &__subscription-card-name {
        font-size: 16px;
        line-height: 19px;
        font-weight: 600;
    }

    &__subscription-card-name-subtext {
        font-size: 14px;
        line-height: 18px;
        color: $slate-01;
        padding-bottom: 12px;

        &--dark {
            color: $pewter;
        }
    }

    &__subscription-card-info {
        margin-bottom: 12px;
        padding: 0 12px 12px;
        border-bottom: 1px solid $fog;

        &--dark {
            border-bottom-color: $pickled-bluewood;
        }

        &:last-child {
            border-bottom: 0;
        }

        &--no-edit {
            margin-bottom: 0;
        }
    }

    &__subscription-card-label {
        font-size: 14px;
        line-height: 17px;
        letter-spacing: 0.25px;
        margin-bottom: 5px;
        color: $slate-03;

        &--dark {
            color: $fog;
        }
    }

    &__subscription-card-title {
        font-size: 16px;
        line-height: 16px;
        margin-bottom: 6px;
    }

    &__subscription-card-subtext {
        font-size: 14px;
        line-height: 18px;
        color: $slate-01;

        &--dark {
            color: $pewter;
        }
    }

    &__subscription-card-edit {
        padding: 0 12px 10px;
        display: block;
    }
}
</style>
